import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiZap, FiMessageSquare, FiTrendingUp } from 'react-icons/fi';
import { chatService } from '../../services/chatService';

interface UsageDashboardProps {
  conversationId?: string;
  userId?: string;
}

const UsageDashboard: React.FC<UsageDashboardProps> = ({ conversationId, userId }) => {
  const [loading, setLoading] = useState(true);
  const [conversationUsage, setConversationUsage] = useState<{
    totalTokens: number;
    totalCost: number;
    messageCount: number;
  } | null>(null);

  const [userUsage, setUserUsage] = useState<{
    totalTokens: number;
    totalCost: number;
    requestCount: number;
    byModel: Record<string, { tokens: number; cost: number; requests: number }>;
  } | null>(null);

  useEffect(() => {
    const loadUsage = async () => {
      setLoading(true);

      try {
        if (conversationId) {
          const convUsage = await chatService.getConversationUsage(conversationId);
          setConversationUsage(convUsage);
        }

        if (userId) {
          const usrUsage = await chatService.getUserUsage(30);
          setUserUsage(usrUsage);
        }
      } catch (error) {
        console.error('Failed to load usage:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsage();
  }, [conversationId, userId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Conversation Usage */}
      {conversationUsage && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation Usage</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              icon={<FiMessageSquare className="w-5 h-5" />}
              label="Messages"
              value={conversationUsage.messageCount.toString()}
              color="blue"
            />
            <StatCard
              icon={<FiZap className="w-5 h-5" />}
              label="Tokens Used"
              value={conversationUsage.totalTokens.toLocaleString()}
              color="purple"
            />
            <StatCard
              icon={<FiDollarSign className="w-5 h-5" />}
              label="Total Cost"
              value={`$${conversationUsage.totalCost.toFixed(4)}`}
              color="green"
            />
          </div>
        </div>
      )}

      {/* User Usage (Last 30 Days) */}
      {userUsage && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Usage (Last 30 Days)</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <StatCard
              icon={<FiMessageSquare className="w-5 h-5" />}
              label="AI Requests"
              value={userUsage.requestCount.toLocaleString()}
              color="blue"
            />
            <StatCard
              icon={<FiZap className="w-5 h-5" />}
              label="Total Tokens"
              value={userUsage.totalTokens.toLocaleString()}
              color="purple"
            />
            <StatCard
              icon={<FiDollarSign className="w-5 h-5" />}
              label="Total Spent"
              value={`$${userUsage.totalCost.toFixed(2)}`}
              color="green"
            />
          </div>

          {/* By Model Breakdown */}
          {Object.keys(userUsage.byModel).length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                <FiTrendingUp className="w-4 h-4" />
                Usage by Model
              </h4>
              <div className="space-y-2">
                {Object.entries(userUsage.byModel).map(([model, stats]) => (
                  <div
                    key={model}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <div className="font-medium text-gray-900">{model}</div>
                      <div className="text-sm text-gray-500">
                        {stats.requests} requests
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        ${stats.cost.toFixed(4)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {stats.tokens.toLocaleString()} tokens
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Cost Breakdown Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Cost Information</h4>
        <div className="text-sm text-blue-800 space-y-1">
          <div>• GPT-4 Turbo: $0.01 per 1K input tokens, $0.03 per 1K output tokens</div>
          <div>• GPT-3.5 Turbo: $0.0015 per 1K input tokens, $0.002 per 1K output tokens</div>
          <div>• Costs are estimates and may vary based on actual usage</div>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: 'blue' | 'purple' | 'green';
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    green: 'bg-green-50 text-green-600 border-green-200',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${colorClasses[color]} border rounded-lg p-4`}
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-white rounded-lg shadow-sm">{icon}</div>
        <div>
          <div className="text-sm font-medium opacity-80">{label}</div>
          <div className="text-2xl font-bold">{value}</div>
        </div>
      </div>
    </motion.div>
  );
};

export default UsageDashboard;
